/* ---------------------------------------------------------------
   NELKUBA  -  main.js
   Form submission, scroll-reveal, header state, mobile nav.
   --------------------------------------------------------------- */

import { VERSION } from './version.js';

// -------------------------------------------------------------
// Footer version pill (auto-bumped on every commit; see scripts/bump-version.mjs)
// -------------------------------------------------------------
const versionEl = document.getElementById('footer-version');
if (versionEl) versionEl.textContent = VERSION;

// -------------------------------------------------------------
// Google Form configuration
// Resolved from https://forms.gle/W7xNdziPBXZ2fMaq8 on 2026-05-26.
// If you change the form's fields, update these IDs:
//   - "Get pre-filled link" in your Google Form
//   - Fill with dummy values, click "Get link"
//   - The resulting URL has entry.NNN=value pairs for each field
// -------------------------------------------------------------
const FORM_CONFIG = {
  formId: '1FAIpQLSc0vpSeIJiOwXRQ1TMZ5TQQUBjnE1n2pJOKatMZWFb7kLB2Dg',
  fields: {
    name:    'entry.908445222',  // "Your Full Name"
    message: 'entry.44265992',   // "Your Question"
  },
  // Google Forms collects emails via a built-in field when "Collect emails"
  // is enabled. Try the value below first; if submissions don't carry the
  // email through, swap to an explicit entry.NNN added to the form.
  emailField: 'emailAddress',
};

const formAction = `https://docs.google.com/forms/d/e/${FORM_CONFIG.formId}/formResponse`;

const RATE_LIMIT = { max: 3, windowMs: 60 * 60 * 1000 };
const RATE_KEY = 'nelkuba_submits';

// -------------------------------------------------------------
// Footer year
// -------------------------------------------------------------
const yearEl = document.getElementById('footer-year');
if (yearEl) yearEl.textContent = new Date().getFullYear();

// -------------------------------------------------------------
// Sticky header scroll state
// -------------------------------------------------------------
const header = document.getElementById('site-header');
let ticking = false;

function updateHeader() {
  if (window.scrollY > 32) header.classList.add('scrolled');
  else header.classList.remove('scrolled');
  ticking = false;
}

window.addEventListener('scroll', () => {
  if (!ticking) {
    window.requestAnimationFrame(updateHeader);
    ticking = true;
  }
}, { passive: true });

updateHeader();

// -------------------------------------------------------------
// Mobile nav toggle
// -------------------------------------------------------------
const navToggle = document.getElementById('nav-toggle');
const mobileNav = document.getElementById('mobile-nav');

if (navToggle && mobileNav) {
  navToggle.addEventListener('click', () => {
    const open = navToggle.getAttribute('aria-expanded') === 'true';
    const next = !open;
    navToggle.setAttribute('aria-expanded', String(next));
    navToggle.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
    if (next) {
      mobileNav.hidden = false;
      mobileNav.setAttribute('data-open', 'true');
    } else {
      mobileNav.removeAttribute('data-open');
      // small delay before hide so the close transition can play if added later
      mobileNav.hidden = true;
    }
  });

  // Auto-close mobile nav when a link is clicked
  mobileNav.addEventListener('click', (e) => {
    if (e.target.tagName === 'A') {
      navToggle.setAttribute('aria-expanded', 'false');
      navToggle.setAttribute('aria-label', 'Open menu');
      mobileNav.removeAttribute('data-open');
      mobileNav.hidden = true;
    }
  });
}

// -------------------------------------------------------------
// Scroll-reveal via IntersectionObserver (one-shot)
// -------------------------------------------------------------
const revealEls = document.querySelectorAll('.reveal');

if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.1,
  });

  revealEls.forEach((el) => io.observe(el));
} else {
  revealEls.forEach((el) => el.classList.add('is-visible'));
}

// -------------------------------------------------------------
// Contact form
// -------------------------------------------------------------
const form = document.getElementById('contact-form');
const statusEl = document.getElementById('form-status');
const submitBtn = document.getElementById('submit-btn');
const iframe = document.getElementById('nelkuba-form-target');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function setFieldError(input, message) {
  const field = input.closest('.field');
  const errorEl = field.querySelector('.field-error');
  if (message) {
    field.classList.add('has-error');
    errorEl.textContent = message;
    input.setAttribute('aria-invalid', 'true');
  } else {
    field.classList.remove('has-error');
    errorEl.textContent = '';
    input.removeAttribute('aria-invalid');
  }
}

function clearAllErrors() {
  form.querySelectorAll('.field.has-error').forEach((f) => {
    f.classList.remove('has-error');
    f.querySelector('.field-error').textContent = '';
    f.querySelector('input, textarea')?.removeAttribute('aria-invalid');
  });
  statusEl.textContent = '';
  statusEl.className = 'form-note';
}

function validate(values) {
  const errors = {};
  if (!values.name || values.name.length < 2) errors.name = 'Please enter your name.';
  if (values.name && values.name.length > 100) errors.name = 'Name is too long.';
  if (!values.email) errors.email = 'Please enter your email.';
  else if (!EMAIL_RE.test(values.email)) errors.email = "That email doesn't look quite right.";
  if (!values.message || values.message.length < 10) errors.message = 'Tell us a little more about your goal.';
  if (values.message && values.message.length > 5000) errors.message = 'Message is too long.';
  return errors;
}

function checkRateLimit() {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(RATE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const recent = list.filter((t) => now - t < RATE_LIMIT.windowMs);
    if (recent.length >= RATE_LIMIT.max) return false;
    recent.push(now);
    localStorage.setItem(RATE_KEY, JSON.stringify(recent));
    return true;
  } catch {
    return true; // localStorage unavailable -> don't block
  }
}

function submitToGoogle(values) {
  // Build a hidden form that POSTs to Google through the iframe.
  const f = document.createElement('form');
  f.action = formAction;
  f.method = 'POST';
  f.target = 'nelkuba-form-target';
  f.style.display = 'none';

  const append = (name, value) => {
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = name;
    i.value = value;
    f.appendChild(i);
  };

  append(FORM_CONFIG.fields.name, values.name);
  append(FORM_CONFIG.emailField, values.email);
  append(FORM_CONFIG.fields.message, values.message);

  document.body.appendChild(f);
  f.submit();
  setTimeout(() => f.remove(), 1000);
}

if (form) {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    clearAllErrors();

    const nameEl = form.querySelector('#f-name');
    const emailEl = form.querySelector('#f-email');
    const messageEl = form.querySelector('#f-message');
    const hpEl = form.querySelector('#hp_company');

    // Honeypot — silently drop bots
    if (hpEl && hpEl.value.trim() !== '') {
      submitBtn.classList.add('is-sent');
      submitBtn.querySelector('.btn-label').textContent = 'Message sent';
      statusEl.textContent = 'Thanks — we got it.';
      statusEl.className = 'form-note is-success';
      return;
    }

    const values = {
      name: nameEl.value.trim(),
      email: emailEl.value.trim(),
      message: messageEl.value.trim(),
    };

    const errors = validate(values);

    if (Object.keys(errors).length) {
      if (errors.name) setFieldError(nameEl, errors.name);
      if (errors.email) setFieldError(emailEl, errors.email);
      if (errors.message) setFieldError(messageEl, errors.message);
      statusEl.textContent = 'Please fix the highlighted fields.';
      statusEl.className = 'form-note is-error';
      // Focus the first invalid field
      const firstError = form.querySelector('.field.has-error input, .field.has-error textarea');
      if (firstError) firstError.focus();
      return;
    }

    if (!checkRateLimit()) {
      statusEl.textContent = 'You’ve sent a few messages already. Try again in an hour.';
      statusEl.className = 'form-note is-error';
      return;
    }

    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.disabled = true;
    submitBtn.querySelector('.btn-label').textContent = 'Sending';
    statusEl.textContent = '';
    statusEl.className = 'form-note';

    submitToGoogle(values);

    // Google's CORS rules prevent us from reading the response. We optimistically
    // show success after a short delay — submissions do land in the Form.
    setTimeout(() => {
      submitBtn.removeAttribute('aria-busy');
      submitBtn.classList.add('is-sent');
      submitBtn.querySelector('.btn-label').textContent = 'Message sent';
      submitBtn.disabled = false;
      statusEl.textContent = 'Thanks — we’ll be in touch within 1–2 business days.';
      statusEl.className = 'form-note is-success';

      // Clear all submitted values + disable briefly so the user doesn't
      // accidentally re-submit while reading the success state.
      form.querySelectorAll('input, textarea').forEach((el) => {
        if (el.id !== 'hp_company') {
          el.value = '';
          el.disabled = true;
        }
      });

      // After ~5s the form returns to a fresh, usable state so the visitor
      // can send another message if they want.
      setTimeout(() => {
        submitBtn.classList.remove('is-sent');
        submitBtn.querySelector('.btn-label').textContent = 'Send message';
        form.querySelectorAll('input, textarea').forEach((el) => {
          el.disabled = false;
        });
      }, 5000);
    }, 700);
  });

  // Clear error on input
  form.querySelectorAll('input, textarea').forEach((el) => {
    el.addEventListener('input', () => {
      if (el.closest('.field')?.classList.contains('has-error')) setFieldError(el, '');
    });
  });
}
